import { storageHelpers, CACHE_KEY_PREFIX, TTL_CONFIG } from "src/storage/mmkv"
import { CacheMonitor } from "./CacheMonitor"

/**
 * 未読返信数キャッシュの型定義
 */
export interface UnreadReplyCountCache {
  version: string
  contactId: string
  count: number
  cachedAt: number
  expiresAt: number
  isOptimisticallyRead?: boolean // 楽観的既読フラグ（連絡詳細で既読にした場合）
  __checksum?: string
  __timestamp?: number
}

/**
 * 未読返信数キャッシュサービス
 *
 * 連絡ごとの未読返信数を個別にキャッシュすることで、
 * バックエンドAPIの変更なしに未読返信数のAPI呼び出しを削減
 */
export class UnreadReplyCountCacheService {
  private static readonly CURRENT_VERSION = "1.0"
  private static readonly CACHE_PREFIX = `${CACHE_KEY_PREFIX.CONTACT_LIST}_unread_reply`
  private static readonly DEFAULT_TTL = TTL_CONFIG.inbox // 24時間
  private static readonly monitor = CacheMonitor.getInstance()

  // キャッシュ更新時のコールバック（useIncrementalUnreadCount用）
  private static updateCallbacks: Array<(contactId: string, newCount: number) => void> = []

  /**
   * キャッシュ更新時のコールバックを登録
   */
  static addUpdateCallback(callback: (contactId: string, newCount: number) => void): void {
    this.updateCallbacks.push(callback)
  }

  /**
   * コールバックを削除
   */
  static removeUpdateCallback(callback: (contactId: string, newCount: number) => void): void {
    this.updateCallbacks = this.updateCallbacks.filter((cb) => cb !== callback)
  }

  /**
   * キャッシュキーを生成
   */
  private static generateCacheKey(contactId: string): string {
    return `${this.CACHE_PREFIX}_${contactId}`
  }

  /**
   * 未読返信数を取得
   */
  static get(contactId: string): number | null {
    const cache = this.getCache(contactId)
    return cache ? cache.count : null
  }

  /**
   * キャッシュオブジェクト全体を取得（楽観的既読フラグを含む）
   */
  static getCache(contactId: string): UnreadReplyCountCache | null {
    try {
      const startTime = Date.now()
      const cacheKey = this.generateCacheKey(contactId)

      const cached = storageHelpers.getObject<UnreadReplyCountCache>(cacheKey)

      if (!cached) {
        this.monitor.recordMiss(Date.now() - startTime)
        return null
      }

      // バージョンチェック
      if (cached.version !== this.CURRENT_VERSION) {
        this.delete(contactId)
        return null
      }

      const now = Date.now()

      // TTL確認
      if (cached.expiresAt <= now) {
        this.delete(contactId)
        this.monitor.recordEviction()
        return null
      }

      // チェックサム検証（あれば）
      if (cached.__checksum) {
        if (!this.verifyChecksum(cached)) {
          console.warn("[UnreadReplyCountCache] Checksum verification failed", { contactId })
          this.delete(contactId)
          this.monitor.recordError(new Error("Checksum verification failed"), "get")
          return null
        }
      }

      this.monitor.recordHit(Date.now() - startTime)
      return cached
    } catch (error) {
      console.error("[UnreadReplyCountCache] Error getting cache", error)
      this.monitor.recordError(error as Error, "get")
      return null
    }
  }

  /**
   * 未読返信数を保存
   */
  static set(contactId: string, count: number): void {
    try {
      const cacheKey = this.generateCacheKey(contactId)
      const now = Date.now()

      const cache: UnreadReplyCountCache = {
        version: this.CURRENT_VERSION,
        contactId,
        count,
        cachedAt: now,
        expiresAt: now + this.DEFAULT_TTL,
        // count === 0の場合、楽観的既読フラグを設定（連絡詳細で既読にした場合）
        isOptimisticallyRead: count === 0 ? true : undefined,
      }

      // チェックサム付きで保存
      const cacheWithChecksum = this.addChecksum(cache)
      storageHelpers.setObject(cacheKey, cacheWithChecksum)

      console.log("[UnreadReplyCountCache] SET", {
        contactId,
        count,
        ttl: `${this.DEFAULT_TTL / 1000 / 60 / 60}h`,
      })

      // コールバック実行（useIncrementalUnreadCountのstate更新用）
      this.updateCallbacks.forEach((callback) => {
        callback(contactId, count)
      })
    } catch (error) {
      console.error("[UnreadReplyCountCache] Error setting cache", error)
      this.monitor.recordError(error as Error, "set")
    }
  }

  /**
   * バッチで未読返信数を保存
   */
  static setBatch(counts: Record<string, number>): void {
    Object.entries(counts).forEach(([contactId, count]) => {
      this.set(contactId, count)
    })
  }

  /**
   * 未読返信数を更新（デクリメント）
   */
  static decrement(contactId: string, amount = 1): void {
    const currentCount = this.get(contactId)
    if (currentCount !== null) {
      const newCount = Math.max(0, currentCount - amount)
      this.set(contactId, newCount)
      console.log("[UnreadReplyCountCache] DECREMENT", {
        contactId,
        before: currentCount,
        after: newCount,
      })
    }
  }

  /**
   * 未読返信数を更新（インクリメント）
   */
  static increment(contactId: string, amount = 1): void {
    const currentCount = this.get(contactId)
    if (currentCount !== null) {
      const newCount = currentCount + amount
      this.set(contactId, newCount)
      console.log("[UnreadReplyCountCache] INCREMENT", {
        contactId,
        before: currentCount,
        after: newCount,
      })
    }
  }

  /**
   * 特定の連絡の未読返信数キャッシュを削除
   */
  static delete(contactId: string): void {
    try {
      const cacheKey = this.generateCacheKey(contactId)
      storageHelpers.delete(cacheKey)
      console.log("[UnreadReplyCountCache] DELETE", { contactId })
    } catch (error) {
      console.error("[UnreadReplyCountCache] Error deleting cache", error)
    }
  }

  /**
   * すべての未読返信数キャッシュを削除
   */
  static clearAll(): void {
    try {
      const pattern = `${this.CACHE_PREFIX}_*`
      storageHelpers.deleteKeysWithPrefix(pattern)
      console.log("[UnreadReplyCountCache] CLEAR_ALL")
    } catch (error) {
      console.error("[UnreadReplyCountCache] Error clearing all caches", error)
    }
  }

  /**
   * チェックサムを追加
   */
  private static addChecksum(data: UnreadReplyCountCache): UnreadReplyCountCache {
    const checksum = this.calculateChecksum(data)
    return {
      ...data,
      __checksum: checksum,
      __timestamp: Date.now(),
    }
  }

  /**
   * チェックサムを検証
   */
  private static verifyChecksum(data: UnreadReplyCountCache): boolean {
    if (!data || !data.__checksum) {
      return false
    }

    const { __checksum, __timestamp, ...originalData } = data
    const calculatedChecksum = this.calculateChecksum(originalData)
    return __checksum === calculatedChecksum
  }

  /**
   * チェックサムを計算
   */
  private static calculateChecksum(data: Partial<UnreadReplyCountCache>): string {
    try {
      const json = JSON.stringify(data)
      let hash = 0
      for (let i = 0; i < json.length; i++) {
        const char = json.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash
      }
      return hash.toString(36)
    } catch (error) {
      console.error("[UnreadReplyCountCache] Error calculating checksum", error)
      return ""
    }
  }

  /**
   * キャッシュ統計情報を取得
   */
  static getStats(): {
    totalKeys: number
    averageAge: number
  } {
    try {
      const allKeys = storageHelpers.getAllKeys()
      const cacheKeys = allKeys.filter((key: string) => key.startsWith(this.CACHE_PREFIX))

      let totalAge = 0
      const now = Date.now()

      cacheKeys.forEach((key: string) => {
        const cached = storageHelpers.getObject<UnreadReplyCountCache>(key)
        if (cached?.cachedAt) {
          totalAge += now - cached.cachedAt
        }
      })

      return {
        totalKeys: cacheKeys.length,
        averageAge: cacheKeys.length > 0 ? totalAge / cacheKeys.length : 0,
      }
    } catch (error) {
      console.error("[UnreadReplyCountCache] Error getting stats", error)
      return {
        totalKeys: 0,
        averageAge: 0,
      }
    }
  }
}
